import { useEffect, useRef, useState, type ReactElement } from "react";
import { isApiError, type FinanceApi } from "@bw/shared/api";
import type { FinanceContextResponse, PayoutPullResponse, PayoutPullStartRequest } from "@bw/shared/types/generated";
import { formatDateTime, formatDateUtc } from "@bw/shared/utils";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { FinanceConnectTrustedActorScope } from "@/components/finance-connect/FinanceConnectEmbed";

export interface PayoutRecoveryPaneProps {
  orgId: string;
  api: FinanceApi;
  context: FinanceContextResponse;
  actorScope: FinanceConnectTrustedActorScope;
  /** Public run locator for real same-run reload; never a provider cursor/key. */
  initialRunId: string | null;
  /** Null clears only the public locator after an explicit new-window action. */
  onRunChanged: (runId: string | null) => void;
  onLedgerChanged: () => void | Promise<void>;
}

/** Read-only provider recovery with exact server progress and memory-only intent. */
export function PayoutRecoveryPane(props: PayoutRecoveryPaneProps): ReactElement {
  const { api, orgId, context, actorScope, initialRunId } = props;
  const read = context.org_id === orgId && actorScope.orgId === orgId &&
    actorScope.bindingGeneration === context.binding_generation && actorScope.workSessionRevision === context.work_session_revision &&
    context.allowed_actions.includes("org:finance_read");
  const dispatch = read && context.account_bound && context.allowed_actions.includes("org:finance_repair") &&
    context.stripe_configuration.state === "configured";
  const scope = JSON.stringify([actorScope, context.authority_generation, context.allowed_actions, context.stripe_configuration]);
  const latest = useRef({ props, scope, read }); latest.current = { props, scope, read };
  const active = useRef(true);
  const [run, setRun] = useState<PayoutPullResponse | null>(null);
  const [runScope, setRunScope] = useState(scope);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [intent, setIntent] = useState<PayoutPullStartRequest | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const accepted = runScope === scope ? run : null;
  const live = (captured: string) => active.current && latest.current.scope === captured && latest.current.read;
  const load = async (identifier: string, captured: string, signal?: AbortSignal) => {
    const result = await api.getPayoutPull(orgId, identifier, signal);
    if (!live(captured)) return;
    if (result.org_id !== orgId || result.run_id !== identifier) throw new Error("Payout recovery unavailable");
    setRun(result); setRunScope(captured);
  };
  useEffect(() => {
    const abort = new AbortController();
    setRun(null); setRunScope(scope); setIntent(null); setError(""); setWorking(false);
    if (read && initialRunId) {
      setWorking(true);
      void load(initialRunId, scope, abort.signal).catch(() => {
        if (!abort.signal.aborted && live(scope)) setError("Payout recovery is unavailable. Reload the recorded run.");
      }).finally(() => { if (!abort.signal.aborted && live(scope)) setWorking(false); });
    }
    return () => abort.abort();
  }, [api, scope, initialRunId, read]);
  const begin = async () => {
    if (!dispatch || working) return;
    const captured = scope;
    let request = intent;
    if (!request) {
      const beginning = new Date(`${start}T00:00:00Z`);
      const ending = new Date(`${end}T00:00:00Z`);
      if (!Number.isFinite(beginning.getTime()) || !Number.isFinite(ending.getTime())) {
        setError("Choose both UTC dates before starting."); return;
      }
      request = { idempotency_key: crypto.randomUUID(), window: { start: beginning.toISOString(), end: ending.toISOString() } };
      setIntent(request);
    }
    setError(""); setWorking(true);
    try {
      const result = await api.startPayoutPull(orgId, request);
      if (!live(captured)) return;
      if (result.org_id !== orgId) throw new Error("Payout recovery unavailable");
      setRun(result); setRunScope(captured);
      latest.current.props.onRunChanged(result.run_id);
    } catch (cause) { if (live(captured)) {
      if (isApiError(cause) && (cause.code === "FINANCE_INVALID_REQUEST" || cause.status === 422)) {
        setIntent(null); setError("Choose a positive UTC window of at most 90 days ending no later than now.");
      } else setError("Start could not be confirmed. Retry the same recorded request.");
    } }
    finally { if (live(captured)) setWorking(false); }
  };
  const advance = async () => {
    if (!accepted || !dispatch || !accepted.allowed_actions.includes("advance") || working) return;
    const captured = scope, identifier = accepted.run_id;
    setError(""); setWorking(true);
    try {
      const result = await api.advancePayoutPull(orgId, identifier, { expected_version: accepted.version });
      if (!live(captured)) return;
      if (result.org_id !== orgId || result.run_id !== identifier) throw new Error("Payout recovery unavailable");
      setRun(result); setRunScope(captured);
      await latest.current.props.onLedgerChanged();
    } catch { if (live(captured)) setError("This page could not be confirmed. Reload this run before retrying."); }
    finally { if (live(captured)) setWorking(false); }
  };
  const reload = async () => {
    const identifier = accepted?.run_id ?? initialRunId;
    if (!identifier || working || !read) return;
    const captured = scope;
    setError(""); setWorking(true);
    try { await load(identifier, captured); }
    catch { if (live(captured)) setError("Payout recovery is unavailable. Reload the recorded run."); }
    finally { if (live(captured)) setWorking(false); }
  };
  const newRecovery = () => {
    if (!dispatch || working || (!accepted && !initialRunId)) return;
    setRun(null); setRunScope(scope); setIntent(null); setStart(""); setEnd(""); setError("");
    latest.current.props.onRunChanged(null);
  };
  if (!read) return <Alert severity="error">Payout recovery access is unavailable.</Alert>;
  return <Stack spacing={1.5} component="section" aria-label="Payout recovery">
    <Typography component="h2" variant="h6">Payout recovery</Typography>
    <Typography variant="body2">Read Stripe payouts into the existing ledger. This does not move money or change your bank account.</Typography>
    {error && <Alert severity="error">{error}</Alert>}
    {context.stripe_configuration.state === "unavailable" && <Alert severity="info">Provider controls are unavailable. Recorded outcomes and reports remain readable.</Alert>}
    {!accepted && dispatch && !initialRunId && <>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <TextField size="small" label="Start date (UTC)" type="date" value={start} onChange={event => setStart(event.target.value)}
          disabled={working || Boolean(intent)} slotProps={{ inputLabel: { shrink: true } }} />
        <TextField size="small" label="End date (UTC)" type="date" value={end} onChange={event => setEnd(event.target.value)}
          disabled={working || Boolean(intent)} slotProps={{ inputLabel: { shrink: true } }} />
      </Stack>
      <Typography variant="body2" color="text.secondary">The end date is excluded. Choose at most 90 days, ending no later than now.</Typography>
      <Button size="small" variant="outlined" disabled={working || (!intent && (!start || !end))} onClick={() => void begin()} sx={{ alignSelf: "flex-start" }}>
        {intent && error ? "Retry pull" : "Start payout recovery"}</Button>
    </>}
    {working && <CircularProgress aria-label="Loading payout recovery" size={20} />}
    {accepted && <>
      <Typography variant="body1">{accepted.status.replaceAll("_", " ")}</Typography>
      <Typography variant="body2">Phase: {accepted.phase}. Checked {formatDateTime(accepted.updated_at)}.</Typography>
      <Typography variant="body2">UTC window: {formatDateUtc(accepted.window.start)} – {formatDateUtc(accepted.window.end)} (end excluded).</Typography>
      <Stack direction="row" sx={{ flexWrap: "wrap", gap: 2 }}>
        {([ ["Seen", accepted.counters.seen], ["Inserted", accepted.counters.inserted], ["Updated", accepted.counters.updated],
          ["Unchanged", accepted.counters.unchanged], ["Stale", accepted.counters.stale], ["Conflicts", accepted.counters.conflicts] ] as const)
          .map(([label, value]) => <Typography key={label} variant="body2">{label}: {value}</Typography>)}
      </Stack>
      {accepted.status === "completed" && <Typography variant="body2">The bounded pull finished. Review Reports for financial reconciliation.</Typography>}
      {accepted.status === "needs_reconciliation" && <Typography variant="body2">Conflicting evidence was retained without forcing a ledger overwrite. Review the recorded reports.</Typography>}
      {accepted.status === "binding_changed" && <Typography variant="body2">The account binding changed. This old run cannot advance.</Typography>}
      {accepted.error_code && <Typography variant="body2">Result code: {accepted.error_code}</Typography>}
    </>}
    <Stack direction="row" spacing={1}>
      {accepted && dispatch && accepted.allowed_actions.includes("advance") && <Button size="small" variant="outlined" disabled={working} onClick={() => void advance()}>Advance next batch</Button>}
      {(accepted || initialRunId) && <Button size="small" disabled={working} onClick={() => void reload()}>Reload run</Button>}
      {dispatch && (accepted || initialRunId) && <Button size="small" disabled={working} onClick={newRecovery}>New recovery</Button>}
    </Stack>
  </Stack>;
}
